import { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useSettings } from "../lib/settings";
import {
  captureReferralFromUrl,
  clearStoredReferralCode,
  lookupReferral,
  readStoredReferralCode,
  referralMessage,
} from "../lib/referral";

/**
 * "Ai primit un cod de recomandare?" — an ordinary, optional field of the
 * public checkout, right before the payment (decided by FPED on 13.10.2026:
 * visible like every other field, not a discreet link and not a box apart).
 *
 * The code is checked against the server while it is typed and the answer is
 * shown under the field. A code that cannot be used never stops an enrolment:
 * the server leaves it out and the form goes through.
 *
 * Nothing renders while `settings.referral_enabled` is off. The parent owns
 * `code` and sends it as `referral_code`.
 */
export default function ReferralCodeField({ code, setCode, email }) {
  const settings = useSettings();
  const enabled = !!settings.referral_enabled;
  const [state, setState] = useState({ phase: "idle", result: null });
  const timer = useRef(null);

  // A code that came with the link fills the field, unless something is typed.
  useEffect(() => {
    if (!enabled) return;
    captureReferralFromUrl();
    const stored = readStoredReferralCode();
    if (stored && !code) setCode(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const check = useCallback(async (raw, address) => {
    const normalized = (raw || "").trim().toUpperCase();
    if (normalized.length < 4) {
      setState({ phase: "idle", result: null });
      return;
    }
    setState({ phase: "loading", result: null });
    const result = await lookupReferral(normalized, address);
    setState({ phase: "done", result });
    // A stored code that turned out unusable is forgotten, not offered again.
    if (!result?.valid && ["not_found", "expired", "cancelled"].includes(result?.reason)
        && readStoredReferralCode() === normalized) {
      clearStoredReferralCode();
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => check(code, email), 450);
    return () => timer.current && clearTimeout(timer.current);
  }, [code, email, enabled, check]);

  if (!enabled) return null;

  const message = state.phase === "done" ? referralMessage(state.result) : null;

  return (
    <div className="mt-6" data-testid="referral-code-field">
      <label htmlFor="checkout-referral-code" className="block">
        <span className="text-xs font-semibold text-[#373A45] uppercase tracking-widest">
          Ai primit un cod de recomandare?
        </span>
        <span className="mt-1 block text-xs text-[#6B7280]">
          Opțional. Trece aici codul primit de la un coleg.
        </span>
        <input
          id="checkout-referral-code"
          name="referral_code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 16))}
          onBlur={() => check(code, email)}
          aria-describedby="checkout-referral-status"
          className="mt-1.5 w-full md:w-1/2 bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-4 py-3 tracking-widest text-[#373A45] focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none"
          data-testid="referral-code-input"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
        />
      </label>
      <div id="checkout-referral-status" aria-live="polite">
        {state.phase === "loading" && (
          <p className="mt-2 flex items-center gap-2 text-xs text-[#6B7280]" data-testid="referral-status-loading">
            <Loader2 size={12} className="animate-spin" /> Se verifică codul…
          </p>
        )}
        {message?.tone === "ok" && (
          <p className="mt-2 flex items-start gap-2 text-sm text-[#2F6B4E]" data-testid="referral-status-valid">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#519872]" />
            <span>
              {message.text} <strong>{message.name}</strong>{message.name.endsWith(".") ? "" : "."}
              {message.benefit && (
                <span className="block" data-testid="referral-status-benefit">Cu acest cod primești: {message.benefit}</span>
              )}
            </span>
          </p>
        )}
        {message?.tone === "warn" && (
          <p className="mt-2 flex items-start gap-2 text-sm text-[#92400E]" data-testid="referral-status-invalid">
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-[#a85a06]" />
            <span>{message.text}</span>
          </p>
        )}
      </div>
    </div>
  );
}
